/**
 * MUI Theme · Folo-aligned
 *
 * 整合 design tokens（tokens.ts）到 MUI Theme，并对核心组件做深度 styleOverrides
 * 抹平 Material 的"圆胖"默认观感。
 *
 * 关键变化（v1 → v2）：
 *   - borderRadius: 18 → 8（克制感）
 *   - 字号节奏：h1-h6 用 typography.* 7 级 scale，不再统一 600 weight
 *   - 阴影：4 档分级（sm/md/lg/xl），暗色加 inset 白边
 *   - Chip 圆角：14 → 6
 *   - Dialog 圆角：18 → 12
 *   - 焦点环：紫色 2px ring + 4px offset
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';

import { themes, ThemeName } from './palette';
import { accent, fontFamily, radius, shadow, transition, typography, zinc } from './tokens';

const getThemeOptions = (
  mode: 'light' | 'dark',
  themeName: ThemeName = 'default',
): ThemeOptions => {
  const palette = themes[themeName][mode];
  const isDark = mode === 'dark';

  // 统一焦点环样式（紫色，圆角对齐）
  const focusRing = {
    outline: `2px solid ${accent[isDark ? 400 : 600]}`,
    outlineOffset: '2px',
  };

  // 卡片基础阴影（暗色额外 inset 白边模拟物理厚度）
  const cardShadow = isDark ? `${shadow.md}, inset 0 1px 0 rgba(255, 255, 255, 0.04)` : shadow.sm;

  const cardHoverShadow = isDark
    ? `${shadow.lg}, inset 0 1px 0 rgba(255, 255, 255, 0.06)`
    : shadow.md;

  return {
    palette: {
      mode,
      ...palette,
    },
    shape: {
      borderRadius: 8, // ← 18 → 8
    },
    typography: {
      fontFamily: fontFamily.sans,
      // 7 级 type scale（取自 tokens.typography）
      h1: typography.display,
      h2: typography.h2,
      h3: typography.h3,
      h4: typography.h4,
      h5: { ...typography.h4, fontWeight: 600 },
      h6: { ...typography.bodyLg, fontWeight: 600 },
      subtitle1: { ...typography.bodyLg, fontWeight: 500 },
      subtitle2: { ...typography.body, fontWeight: 600 },
      body1: typography.body,
      body2: typography.bodySm,
      caption: typography.caption,
      overline: {
        ...typography.caption,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 600,
      },
      button: {
        ...typography.bodySm,
        fontWeight: 500,
        textTransform: 'none',
        letterSpacing: '0',
      },
    },
    components: {
      // ── Buttons ────────────────────────────────────────────────────────────
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: parseInt(radius.md), // 8
            padding: '6px 14px',
            fontSize: typography.bodySm.fontSize,
            fontWeight: 500,
            transition: transition.base,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            '&.Mui-focusVisible': focusRing,
          },
          sizeLarge: { padding: '10px 20px' },
          sizeSmall: { padding: '4px 10px', fontSize: '0.8125rem' },
          containedPrimary: {
            backgroundColor: accent[isDark ? 500 : 600],
            color: '#ffffff',
            '&:hover': {
              backgroundColor: accent[isDark ? 400 : 700],
            },
          },
          outlined: {
            borderColor: isDark ? zinc[700] : zinc[300],
            '&:hover': {
              borderColor: isDark ? zinc[600] : zinc[400],
              backgroundColor: isDark ? zinc[800] : zinc[100],
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: parseInt(radius.md),
            transition: transition.base,
            '&.Mui-focusVisible': focusRing,
            '&:hover': {
              backgroundColor: isDark ? zinc[800] : zinc[100],
            },
          },
        },
      },

      // ── Surfaces ───────────────────────────────────────────────────────────
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: parseInt(radius.lg), // 12
            backgroundColor: palette.background.paper,
            backgroundImage: 'none',
            border: `1px solid ${isDark ? zinc[800] : zinc[200]}`,
            boxShadow: cardShadow,
            transition: `${transition.base}, transform ${transition.base.split(' ')[1]} ${transition.base.split(' ')[2]}`,
            '&:hover': {
              boxShadow: cardHoverShadow,
              borderColor: isDark ? zinc[700] : zinc[300],
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: parseInt(radius.lg),
            backgroundColor: palette.background.paper,
            backgroundImage: 'none',
            border: `1px solid ${isDark ? zinc[800] : zinc[200]}`,
          },
          elevation1: { boxShadow: cardShadow },
          elevation2: { boxShadow: shadow.md },
          elevation3: { boxShadow: shadow.lg },
          elevation4: { boxShadow: shadow.xl },
        },
      },

      // ── Form ───────────────────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: parseInt(radius.sm), // 6 → 4 更克制
            fontWeight: 500,
            fontSize: typography.caption.fontSize,
            height: 24,
          },
          sizeSmall: { height: 20, fontSize: '0.7rem' },
          colorPrimary: {
            backgroundColor: isDark ? 'rgba(139,92,246,0.15)' : accent[100],
            color: accent[isDark ? 300 : 700],
            border: 'none',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: parseInt(radius.md),
              backgroundColor: isDark ? zinc[900] : '#ffffff',
              transition: transition.base,
              '& fieldset': {
                borderColor: isDark ? zinc[700] : zinc[300],
                transition: transition.base,
              },
              '&:hover fieldset': {
                borderColor: isDark ? zinc[600] : zinc[400],
              },
              '&.Mui-focused fieldset': {
                borderColor: accent[isDark ? 400 : 600],
                borderWidth: '1.5px',
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: parseInt(radius.md),
          },
        },
      },

      // ── Dialog & Menu ──────────────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: parseInt(radius.xl), // 16
            backgroundImage: 'none',
            border: `1px solid ${isDark ? zinc[800] : zinc[200]}`,
            boxShadow: shadow.xl,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: parseInt(radius.lg),
            border: `1px solid ${isDark ? zinc[800] : zinc[200]}`,
            boxShadow: shadow.lg,
            marginTop: 4,
          },
          list: { padding: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: parseInt(radius.sm),
            margin: '1px 0',
            fontSize: typography.bodySm.fontSize,
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(139,92,246,0.15)' : accent[50],
              color: accent[isDark ? 300 : 700],
            },
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(9, 9, 11, 0.72)' : 'rgba(0, 0, 0, 0.40)',
            backdropFilter: 'blur(4px)',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? zinc[800] : zinc[900],
            color: zinc[50],
            fontSize: '0.75rem',
            fontWeight: 500,
            padding: '6px 10px',
            borderRadius: parseInt(radius.sm),
          },
        },
      },

      // ── Misc ───────────────────────────────────────────────────────────────
      MuiButtonBase: {
        styleOverrides: {
          root: {
            transition: transition.base,
            '&.Mui-focusVisible': focusRing,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: palette.divider },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: {
          root: {
            color: accent[isDark ? 300 : 600],
            textDecorationColor: 'transparent',
            transition: transition.base,
            '&:hover': {
              textDecorationColor: accent[isDark ? 300 : 600],
              color: accent[isDark ? 200 : 700],
            },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? zinc[800] : zinc[200],
            backgroundImage: 'none',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: typography.bodySm.fontSize,
            fontWeight: 500,
            minHeight: 36,
            padding: '6px 12px',
            borderRadius: parseInt(radius.sm),
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 36 },
          indicator: {
            height: 2,
            borderRadius: 2,
            backgroundColor: accent[isDark ? 400 : 600],
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: parseInt(radius.md),
            border: `1px solid`,
            backgroundImage: 'none',
            fontSize: typography.bodySm.fontSize,
          },
          standardInfo: {
            backgroundColor: isDark ? 'rgba(59,130,246,0.10)' : 'rgba(59,130,246,0.06)',
            borderColor: isDark ? 'rgba(59,130,246,0.30)' : 'rgba(59,130,246,0.20)',
          },
          standardWarning: {
            backgroundColor: isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.06)',
            borderColor: isDark ? 'rgba(245,158,11,0.30)' : 'rgba(245,158,11,0.20)',
          },
          standardError: {
            backgroundColor: isDark ? 'rgba(239,68,68,0.10)' : 'rgba(239,68,68,0.06)',
            borderColor: isDark ? 'rgba(239,68,68,0.30)' : 'rgba(239,68,68,0.20)',
          },
          standardSuccess: {
            backgroundColor: isDark ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.06)',
            borderColor: isDark ? 'rgba(16,185,129,0.30)' : 'rgba(16,185,129,0.20)',
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-track': {
              borderRadius: 999,
            },
          },
        },
      },
    },
  };
};

export const getTheme = (mode: 'light' | 'dark', themeName: ThemeName = 'default') => {
  return createTheme(getThemeOptions(mode, themeName));
};
